import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    useDisclosure,
} from '@chakra-ui/react';
import { useState, useEffect, useRef } from 'react';
import StatusBadge from './StatusBadge';
import { formatDate } from 'utils/utils';
import { useGetReports } from 'hooks/useReport';
import { useSelector } from 'react-redux';

function ReportDetailsModal({ isOpen, onClose, report, onEdit }) {
    if (!report) return null;
    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
            <ModalOverlay bg="blackAlpha.600" />
            <ModalContent borderRadius="lg" maxW="650px">
                <ModalHeader
                    fontWeight="bold"
                    color="#1B3B5F"
                    borderBottom="1px solid #EAECF0"
                    pb={3}>
                    Report Details
                </ModalHeader>
                <ModalCloseButton top={3} />
                <ModalBody py={4}>
                    <Text fontWeight="bold" fontSize="lg" mb={4} color="#1B3B5F">
                        {report.customerName}
                    </Text>
                    <Box
                        as="dl"
                        display="grid"
                        gridTemplateColumns="140px 1fr"
                        rowGap={3}
                        columnGap={4}
                        mb={4}>
                        <Text color="#667085" fontWeight="500" fontSize="sm">
                            Customer Email
                        </Text>
                        <Text fontSize="sm">{report.customerEmail}</Text>
                        <Text color="#667085" fontWeight="500" fontSize="sm">
                            Follow-up Date
                        </Text>
                        <Text fontSize="sm">{formatDate(new Date(report.followUpDate))}</Text>
                        <Text color="#667085" fontWeight="500" fontSize="sm">
                            Status
                        </Text>
                        <Box>
                            <StatusBadge status={report.status?.toUpperCase()} />
                        </Box>
                        <Text color="#667085" fontWeight="500" fontSize="sm">
                            Priority
                        </Text>
                        <Box>
                            <StatusBadge status={report.priority?.toUpperCase()} />
                        </Box>
                        <Text color="#667085" fontWeight="500" fontSize="sm">
                            Feedback
                        </Text>
                        <Text fontSize="sm">{report.feedback}</Text>
                        <Text color="#667085" fontWeight="500" fontSize="sm">
                            Follow-up Next Step
                        </Text>
                        <Text fontSize="sm">{report.followUpNextStep}</Text>
                        {report.images && report.images.length > 0 && (
                            <>
                                <Text color="#667085" fontWeight="500" fontSize="sm">
                                    Images
                                </Text>
                                <Text fontSize="sm">{report.images.length} file(s)</Text>
                            </>
                        )}
                    </Box>
                </ModalBody>
                <ModalFooter borderTop="1px solid #EAECF0" pt={3} gap={2}>
                    {/* <Button
                        size="sm"
                        color="#1b3b5f"
                        bg="#B8C1D380"
                        borderRadius="8px"
                        px={6}
                        fontWeight="medium"
                        onClick={() => {
                            onEdit(report);
                            onClose();
                        }}>
                        Edit Report
                    </Button> */}
                    <Button
                        size="sm"
                        color="white"
                        bg="#1b3b5f"
                        borderRadius="8px"
                        px={6}
                        fontWeight="medium"
                        onClick={onClose}>
                        Close
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

const CustomerReports = ({ customer, refreshTrigger, onEdit }) => {
    const verifyReducer = useSelector((state) => state.verifyReducer);
    const { verifiedUser } = verifyReducer;
    const [selectedReport, setSelectedReport] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        reports,
        isLoading: reportsLoading,
        refetch,
        loadMore,
        hasMore,
        totalCount,
    } = useGetReports(verifiedUser?.data?.user?.email, customer?.email);
    console.log('CustomerReports render - reports:', reports);

    // Refetch reports when refreshTrigger changes
    useEffect(() => {
        if (refreshTrigger > 0) {
            refetch();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTrigger]);

    const containerRef = useRef(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            if (container) {
                const { scrollTop, scrollHeight, clientHeight } = container;
                if (scrollTop + clientHeight >= scrollHeight - 20 && !reportsLoading && hasMore) {
                    loadMore();
                }
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [reportsLoading, hasMore, loadMore]);

    const handleRowClick = (report) => {
        setSelectedReport(report);
        onOpen();
    };

    if (reportsLoading && reports.length === 0) {
        return <Box>Loading reports...</Box>;
    }

    if (!reportsLoading && reports.length === 0) {
        return (
            <Box
                minH={'30vh'}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                color={'#667085'}>
                No reports found for this customer
            </Box>
        );
    }

    return (
        <Box>
            <Text fontSize="sm" color="#667085" mb={2}>
                Loaded {reports.length} of {totalCount} reports
            </Text>
            <Box
                ref={containerRef}
                maxH="60vh"
                overflowY="auto"
                border="1px solid #EAECF0"
                borderRadius="lg"
                position="relative">
                <Table variant="simple" size="sm">
                    <Thead bg={'#F9FAFB'} position="sticky" top={0} zIndex={1}>
                        <Tr>
                            <Th textAlign={'center'} py="4">
                                S/N
                            </Th>
                            <Th textAlign={'center'} py="4">
                                Follow-up Date
                            </Th>
                            <Th textAlign={'center'} py="4">
                                Status
                            </Th>
                            <Th textAlign={'center'} py="4">
                                Priority
                            </Th>
                            <Th textAlign={'center'} py="4">
                                Feedback
                            </Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {reports?.map((report, index) => (
                            <Tr
                                fontSize="xs"
                                key={report.id}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleRowClick(report)}
                                _hover={{ bg: '#F9FAFB' }}>
                                <Td borderRight="1px solid #EAECF0" py="4" textAlign={'center'}>
                                    {index + 1}
                                </Td>
                                <Td borderRight="1px solid #EAECF0" py="4" textAlign={'center'}>
                                    <Text>
                                        {report?.followUpDate
                                            ? formatDate(new Date(report?.followUpDate))
                                            : 'N/A'}
                                    </Text>
                                </Td>
                                <Td borderRight="1px solid #EAECF0" py="4" textAlign={'center'}>
                                    <StatusBadge
                                        status={report?.status?.toUpperCase() ?? 'PENDING'}
                                    />
                                </Td>
                                <Td borderRight="1px solid #EAECF0" py="4" textAlign={'center'}>
                                    <StatusBadge
                                        status={report?.priority?.toUpperCase() ?? 'PENDING'}
                                    />
                                </Td>
                                <Td py="4" textAlign={'left'} maxW="300px">
                                    <Text noOfLines={2}>{report?.feedback ?? 'N/A'}</Text>
                                </Td>
                            </Tr>
                        ))}
                        {reportsLoading && reports.length > 0 && (
                            <Tr>
                                <Td colSpan={5} py={4} textAlign="center">
                                    <Text>Loading more reports...</Text>
                                </Td>
                            </Tr>
                        )}
                    </Tbody>
                </Table>
            </Box>
            <ReportDetailsModal
                isOpen={isOpen}
                onClose={onClose}
                report={selectedReport}
                onEdit={onEdit}
            />
        </Box>
    );
};

export default CustomerReports;
